// StyleSheet for the plate calculator — shared by PlateCalcModal and the
// PlateCalcInputsSection it renders, which is why the sheet is passed down as a
// prop rather than each component making its own: one modal, one style home.
//
// Shrank by ~64 lines in LG-37b fetta A: the hand-rolled `barChip*` and
// `signChip*` families were two reimplementations of the shared `Chip`
// primitive, which is the thing that exists to stop exactly that. The
// inventory's ± buttons are still local on purpose — see the note in
// PlateCalcInputsSection.
import { StyleSheet } from 'react-native';
import { webTapFloor } from '../theme';
import type { useTheme } from './theme';

export function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  // The look of a ± button, once. The two that use it differ ONLY in size —
  // the inventory's stays 36 and reaches the tap floor with hitSlop, the
  // compose grid's is 44 for real because nine controls in a row leave no
  // room for invisible skirts. Everything else about them is the same button,
  // and re-typing six properties is how they stop being the same button.
  const stepButtonSkin = {
    borderRadius: r.r3,
    backgroundColor: p.surface,
    borderColor: p.border,
    borderWidth: 1,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  };
  return StyleSheet.create({
    safe: { flex: 1 },
    flex: { flex: 1 },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
    },
    cancel: { color: p.ink2, fontSize: t.sizes.base },
    apply: {
      color: p.brand500,
      fontSize: t.sizes.base,
      fontWeight: '700',
    },
    title: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: '600',
    },
    headerSpacer: { width: 56 },
    body: {
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s4,
    },
    inputRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
    },
    inputLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      // ⭐ LG-93 — una larghezza fissa qui TAGLIA il numero sugli schermi
      // stretti. Misurato a 320: l'etichetta a 88 più i tre bottoni da 36 e i
      // gap lasciano 60pt al campo, di cui 34 utili — e «137.5» a corpo 20 ne
      // vuole il doppio. ⛔ Il numero è il contenuto, l'etichetta lo nomina: a
      // cedere dev'essere lei.
      flexShrink: 1,
      minWidth: 40,
      width: 88,
    },
    input: {
      flex: 1,
      // 🪤 Senza questo il campo NON si restringe su react-native-web: un flex
      // item non scende sotto la larghezza del proprio contenuto finche' il suo
      // min-width e' `auto`, e il `contentContainerStyle` dello ScrollView non
      // vincola la larghezza — quindi la RIGA cresce e spinge fuori schermo il
      // suffisso dell'unita' e l'icona della calcolatrice. Misurato a 360pt col
      // banco: input [38→360], «kg» [368→383], icona [391→435], cioe' i due
      // controlli oltre il bordo.
      // ⛔ Non e' cosmetico da quando il modal parla l'unita' della riga
      // (LG-37b E1): il suffisso e' l'unica cosa che distingue 135 lb da 135 kg,
      // ed e' il difetto dell'inbox `66c51036` a un livello piu' in basso.
      // ⚠️ Su native questo e' gia' il default ⇒ il telefono non cambia di un
      // pixel; era una guardia web, come `webTapFloor` su Chip.
      //
      // ⛔ LG-93 — il pavimento è salito da 0 a 72, e il motivo è l'opposto di
      // quello di prima: `flex: 1` vuol dire anche «stringiti per primo», così
      // a 320 il campo scendeva a 60pt e mostrava «6» al posto di «60».
      // Misurato al banco, non dedotto. ⇒ il campo sta fra 72 e 96 e NON cede;
      // a cedere è l'etichetta, che ha `flexShrink: 1`. A 320 il conto torna:
      // 40 + 8 + 72 + 8 + 36 + 8 + 36 + 8 + 36 = 252 su 288 disponibili.
      // ⚠️ La guardia web sopravvive nel `flexShrink: 0`: il campo non si
      // allarga oltre il tetto e non spinge più nulla fuori riga.
      minWidth: 72,
      flexShrink: 0,
      flexBasis: 72,
      // ⭐ LG-93 — un TETTO, perche' `flex: 1` da solo si prende tutto quello
      // che avanza. Sua parola dell'11/09: «15 caratteri per il peso sono
      // troppi, taglia». Un peso ne vuole cinque («137.5»), e lo spazio che si
      // libera e' esattamente quello che serve allo switcher dell'unita' e al
      // bottone di arrotondamento che gli stanno accanto.
      // ⚠️ `maxWidth` e non `width`: sotto i 96 il campo deve ancora potersi
      // stringere, o su uno schermo stretto torna a spingere fuori i controlli
      // — che e' il difetto che il `minWidth: 0` qui sopra esiste per evitare.
      maxWidth: 96,
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      color: p.ink,
      fontSize: t.sizes.lg,
      fontVariant: ['tabular-nums'],
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
    },
    sectionLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    // I due chip del segno erano due METÀ a tutta larghezza (`flex: 1` sul
    // vecchio `signChip`). `Chip` accetta uno `style` di solo layout, quindi la
    // proporzione si conserva senza forkare il primitivo.
    chipFill: { flex: 1 },
    // I chip della forma si dividono la riga e allineano a SINISTRA, come il
    // mockup approvato (`.chip.mode { flex: 1 1 0; align-items: flex-start }`):
    // con due righe di testo il centraggio le fa ballare l'una sull'altra.
    // Solo layout, che è ciò per cui `Chip.style` esiste.
    chipShape: { flex: 1, alignItems: 'flex-start' as const, minWidth: 88 },
    chipRow: {
      flexDirection: 'row',
      gap: s.s3,
      paddingVertical: s.s2,
    },
    outputCard: {
      backgroundColor: p.surface2,
      borderRadius: r.r3,
      padding: s.s4,
      gap: s.s2,
    },
    outputHeader: {
      color: p.ink,
      fontSize: t.sizes.lg,
      fontWeight: '700',
      letterSpacing: -0.2,
      fontVariant: ['tabular-nums'],
    },
    outputBody: {
      color: p.ink2,
      fontSize: t.sizes.base,
    },
    plateRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
      paddingVertical: s.s2,
    },
    plateKg: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontVariant: ['tabular-nums'],
      fontWeight: '600',
      // Takes the slack so the count and its "−" stay right-aligned as a pair,
      // which is what `justifyContent: space-between` did before the button
      // arrived and made the row three things instead of two.
      flex: 1,
    },
    platePairs: {
      color: p.ink3,
      fontSize: t.sizes.sm,
    },
    remainder: {
      color: p.warn,
      fontSize: t.sizes.sm,
      marginTop: s.s2,
    },
    singleSideHint: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      paddingHorizontal: s.s2,
    },
    configCard: {
      backgroundColor: p.surface2,
      borderRadius: r.r3,
      padding: s.s4,
      gap: s.s2,
    },
    // La riga della barra personalizzata, che compare SOTTO i chip quando
    // l'atleta tocca «personalizzata». Una riga a sé e non un chip che si
    // allarga: un campo dentro uno ScrollView orizzontale sarebbe un campo che
    // scorre via mentre ci si scrive dentro.
    customBarRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
      paddingTop: s.s2,
    },
    // 🪤 `minWidth: 0` per la stessa ragione del campo del bersaglio: su
    // react-native-web un flex item non scende sotto la larghezza del proprio
    // contenuto finché il suo min-width è `auto`, e la riga cresce fuori
    // schermo. Su native è già il default ⇒ il telefono non cambia di un pixel.
    customBarField: { flex: 1, minWidth: 0 },
    // Il paio non-colore obbligatorio di `TextField.invalid`: il bordo rosso da
    // solo è un segnale di solo colore, che è sotto il pavimento a11y.
    fieldError: {
      color: p.danger,
      fontSize: t.sizes.sm,
    },
    inventoryRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
      paddingVertical: s.s2,
    },
    inventoryKg: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontVariant: ['tabular-nums'],
      fontWeight: '600',
      flex: 1,
    },
    stepButton: {
      ...stepButtonSkin,
      // 36 is the visual size and stays 36; the ≥44 target is reached by
      // hitSlop on native. ⚠️ react-native-web's Pressable ignores `hitSlop`
      // ENTIRELY (DS-48), so on web-rnw — where this modal also runs, see
      // plateConfig.web.ts — that floor never arrived. Same guard `Chip.base`
      // uses: `webTapFloor` is undefined on native, so the shipped mobile look
      // is untouched.
      width: webTapFloor ?? 36,
      height: webTapFloor ?? 36,
    },
    stepButtonPressed: {
      backgroundColor: p.surface2,
    },
    // A disabled step button has to LOOK disabled. The inventory's ± shipped
    // without this and read as pressable at zero; the compose block below
    // sits beside it, so leaving the two to fade differently would say the
    // two minus signs mean different things.
    stepButtonDisabled: {
      opacity: 0.5,
    },
    stepButtonText: {
      color: p.ink,
      fontSize: t.sizes.lg,
      fontWeight: '700',
    },
    inventoryPairs: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      width: 80,
      textAlign: 'center',
      fontVariant: ['tabular-nums'],
    },
    signRow: {
      gap: s.s2,
    },
    // The compose block (LG-37b fetta B). The mockup wraps these rather than
    // scrolling them (`.plates { flex-wrap: wrap }`): every denomination the
    // gym owns has to be reachable without a horizontal drag, because this is
    // the row you tap repeatedly while standing at the bar.
    composeSection: {
      gap: s.s2,
    },
    // ONE row: «deve starci tutto su una linea» (Antenore 10/09). The seven
    // commercial denominations divide the width between them and fit a phone.
    //
    // ⚠️ `flexWrap` is here as a FLOOR, not as a layout: paired with the chip's
    // `minWidth: 44` it means an eighth denomination pushes onto a second line
    // instead of squeezing all eight to 37pt, which is what dividing the row
    // equally would do below 393pt. Eight happens only to an athlete carrying
    // the 0.5 kg plate from the old defaults, and zeroing it in the inventory
    // brings the single line back — his own words: «può essere accettabile».
    // ⛔ The wrap is inert at seven; it must never become the normal case.
    composeRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: s.s1,
      paddingVertical: s.s2,
    },
    // ⚠️ 44 REAL, and no hitSlop anywhere in this block — which is a departure
    // from the house rule (36 visual + hitSlop 8) and the reason is the grid
    // itself. Every other 36pt control in the app has empty space around it,
    // so an invisible 8pt skirt costs nothing. Here nine controls sit shoulder
    // to shoulder and two of them are OPPOSITES: with hitSlop the add-zone and
    // the remove-zone overlapped by ~14pt, and a tap in that band did the
    // reverse of what it looked like. Growing the boxes instead of their
    // skirts is what makes the target and the tap the same shape — and it is
    // the only version that works on web-rnw at all, where hitSlop does not
    // exist (DS-48).
    composeChip: {
      // `flex: 1` shares the row out evenly; `minWidth: 44` is where the
      // sharing STOPS. Without the floor, one extra denomination silently
      // takes every chip below the tap target — correct-looking on a 460pt
      // bench and 37pt wide on a 360pt phone, which is exactly the kind of
      // defect no gate reads. The primitive's own horizontal padding is 12 a
      // side and leaves "1.25" nowhere to go at this width, so this narrows
      // it. Layout only — the box and the treatment stay `Chip`'s.
      // `maxWidth` is the other end of the same guard: a chip that wraps onto
      // a line of its own would otherwise stretch across the whole screen and
      // read as a button, not as one weight among seven.
      flex: 1,
      minWidth: 44,
      maxWidth: 88,
      paddingHorizontal: s.s2,
      minHeight: 44,
    },
    composeStepButton: {
      ...stepButtonSkin,
      width: 44,
      height: 44,
    },
    // The calculator (LG-37b fetta C). It is an EXPANSION inside this modal,
    // not a second modal over it: the working value has to stay in sight while
    // you add up the plates you see on the machine, because that value is
    // where the answer is going.
    calcSection: {
      gap: s.s2,
      borderTopColor: p.border,
      borderTopWidth: 1,
      paddingTop: s.s4,
    },
    // Tape and display, right-aligned like the mockup: the digits grow
    // leftwards from a fixed edge, so the number does not walk across the
    // screen as it gets longer.
    // ⚠️ `base`, not the `sm` every other hint in this file uses — and the
    // difference is that this is not a hint. With no `=` key the tape is the
    // ONLY way to check what you typed before trusting the number under it,
    // so it is working text, and reduced-vision legibility is a floor.
    calcTape: {
      color: p.ink3,
      fontSize: t.sizes.base,
      textAlign: 'right',
      fontVariant: ['tabular-nums'],
      minHeight: 18,
    },
    calcDisplay: {
      color: p.ink,
      fontSize: t.sizes['2xl'],
      fontWeight: '700',
      textAlign: 'right',
      fontVariant: ['tabular-nums'],
      borderBottomColor: p.brand500,
      borderBottomWidth: 2,
      paddingBottom: s.s2,
    },
    keypadRow: {
      flexDirection: 'row',
      gap: s.s1,
    },
    // ⚠️ 44 REAL again, no hitSlop — same reason as the compose grid above and
    // worse: sixteen controls shoulder to shoulder, where `÷` sits next to `9`
    // and `+` under `−`. An invisible skirt between two keys means a tap near
    // the seam enters the neighbour's character
    // [[feedback_hitslop_between_opposite_controls_is_a_trap]]. `flex: 1`
    // shares the row four ways and `minWidth` is where the sharing stops.
    calcKey: {
      ...stepButtonSkin,
      flex: 1,
      minWidth: 44,
      minHeight: 44,
    },
    calcKeyPressed: { backgroundColor: p.surface2 },
    calcKeyText: {
      color: p.ink,
      fontSize: t.sizes.lg,
      fontWeight: '600',
      fontVariant: ['tabular-nums'],
    },
    // ⛔ The mockup paints the operators with brand-coloured TEXT
    // (`.key.op { color: var(--app-brand) }`). Not transposable: brand on the
    // untinted background measures 2.94:1 in the light theme, under the 4.5:1
    // floor — the same reason `workoutsStyles.ts:226` refuses it. The tinted
    // FILL carries the distinction instead (the house's brand-surface pair,
    // as `CuePill` uses it), and the glyph says it again without colour.
    calcKeyOp: {
      backgroundColor: p.brand100,
      borderColor: p.brand500,
    },
    calcKeyOpText: { color: p.brandText },
    calcExits: {
      flexDirection: 'row',
      gap: s.s2,
      paddingTop: s.s2,
    },
    // ⚠️ NOT a 50/50 split, and the bench is what said so. «Copia nel peso»
    // fits half a 360pt row in Italian and in English, and does NOT in French
    // («Copier dans le poids») or German («Ins Gewicht übernehmen»): both
    // wrapped to two lines, which also left-aligns the label while the
    // one-line neighbour stays centred, so the row read as broken rather than
    // as tight. Widening the ACTION instead of shortening five translations
    // fixes every language at once — and it is the honest hierarchy anyway:
    // the exit that writes something deserves more room than the one that
    // changes nothing.
    calcExit: { flex: 1 },
    calcExitPrimary: { flex: 2 },
  });
}
